import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Sparkles, Globe, ChevronRight, Check, Search, FileText, TrendingUp, Menu, X, Building2, Link2, MessageSquare } from 'lucide-react';

// Niches for rotating hero headline
const rotatingNiches = [
  { text: "Martial Arts School", emoji: "🥋" },
  { text: "HVAC Company", emoji: "❄️" },
  { text: "Dental Practice", emoji: "🦷" },
  { text: "Real Estate Agent", emoji: "🏠" },
  { text: "Personal Injury Lawyer", emoji: "⚖️" },
  { text: "Med Spa", emoji: "💆" },
  { text: "Roofing Contractor", emoji: "🏗️" },
  { text: "Chiropractor", emoji: "🦴" },
  { text: "Plumber", emoji: "🔧" },
  { text: "Financial Advisor", emoji: "💼" }
];

// Business examples for rotating showcase
const businessExamples = [
  {
    name: "Martial Arts School",
    icon: "🥋",
    searches: [
      { type: "Question", volume: "2,400", query: "How much does martial arts cost for kids?" },
      { type: "Comparison", volume: "1,800", query: "Karate vs Taekwondo for 5 year old" },
      { type: "Local", volume: "900", query: "Best martial arts school Austin Texas" }
    ],
    blogTitle: "How Much Does Martial Arts Cost for Kids in Austin? (Complete 2025 Guide)"
  },
  {
    name: "HVAC Company",
    icon: "❄️",
    searches: [
      { type: "Question", volume: "3,200", query: "How much does AC repair cost?" },
      { type: "Comparison", volume: "2,100", query: "Central air vs mini split cost" },
      { type: "Local", volume: "1,400", query: "Best HVAC company near me" }
    ],
    blogTitle: "How Much Does AC Repair Cost in 2025? (Complete Pricing Guide)"
  },
  {
    name: "Dental Practice",
    icon: "🦷",
    searches: [
      { type: "Question", volume: "4,100", query: "How much do dental implants cost?" },
      { type: "Comparison", volume: "2,800", query: "Invisalign vs braces for adults" },
      { type: "Local", volume: "1,900", query: "Best dentist accepting new patients" }
    ],
    blogTitle: "How Much Do Dental Implants Cost? (2025 Price Breakdown)"
  },
  {
    name: "Real Estate Agent",
    icon: "🏠",
    searches: [
      { type: "Question", volume: "5,400", query: "How much are closing costs when buying a house?" },
      { type: "Comparison", volume: "3,100", query: "Renting vs buying first home" },
      { type: "Local", volume: "2,200", query: "Best neighborhoods in Austin for families" }
    ],
    blogTitle: "How Much Are Closing Costs in Texas? (Complete 2025 Buyer's Guide)"
  },
  {
    name: "Personal Injury Lawyer",
    icon: "⚖️",
    searches: [
      { type: "Question", volume: "2,900", query: "How much is my car accident case worth?" },
      { type: "Comparison", volume: "1,600", query: "Settlement vs going to trial" },
      { type: "Local", volume: "1,100", query: "Best personal injury lawyer near me" }
    ],
    blogTitle: "How Much Is My Car Accident Case Worth? (2025 Settlement Guide)"
  }
];

const LandingPage = ({ user, onLogin, onLogout }) => {
  const navigate = useNavigate();
  const [currentExample, setCurrentExample] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leadEmail, setLeadEmail] = useState('');
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [leadSuccess, setLeadSuccess] = useState(false);
  const [currentNicheIndex, setCurrentNicheIndex] = useState(0);
  const [isNicheFading, setIsNicheFading] = useState(false);

  // Auto-rotate niches in hero headline every 2 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setIsNicheFading(true);
      setTimeout(() => {
        setCurrentNicheIndex((prev) => (prev + 1) % rotatingNiches.length);
        setIsNicheFading(false);
      }, 300); // Fade out duration
    }, 2500); // Change every 2.5 seconds
    return () => clearInterval(interval);
  }, []);

  // Auto-rotate examples every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentExample((prev) => (prev + 1) % businessExamples.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleGetStarted = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      onLogin();
    }
  };

  const handleUpgrade = async (plan) => {
    if (!user) {
      onLogin();
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: plan,
          user_id: user.id,
          origin_url: window.location.origin
        })
      });
      
      const data = await response.json();
      
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Error creating checkout session. Please try again.');
      }
    } catch (error) {
      console.error('Upgrade error:', error);
      alert('Error creating checkout session. Please try again.');
    }
  };

  const handleLeadMagnetSubmit = async (e) => {
    e.preventDefault();
    if (!leadEmail) return;
    
    setLeadSubmitting(true);
    try {
      // Subscribe email
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead-magnet/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: leadEmail })
      });
      
      // Trigger download
      window.open(`${process.env.REACT_APP_BACKEND_URL}/api/lead-magnet/download`, '_blank');
      
      setLeadSuccess(true);
      setLeadEmail('');
    } catch (error) {
      console.error('Lead magnet error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setLeadSubmitting(false);
    }
  };

  const example = businessExamples[currentExample];

  return (
    <div className="landing-page">
      <Helmet>
        <title>QuillFire | Get Found on Google & AI Search | AI Answer Post Generator</title>
        <meta name="description" content="Discover what your customers are actually searching for. Generate SEO-optimized answer posts in minutes. Get found on Google AND AI search like ChatGPT & Perplexity. Start free today." />
        <meta property="og:title" content="QuillFire | Get Found on Google & AI Search" />
        <meta property="og:description" content="Discover what your customers are searching for. Generate SEO-optimized answer posts in minutes. Get found on Google AND AI search." />
        <link rel="canonical" href="https://quillfire.ai" />
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100" data-testid="main-navigation">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img src="/quillfire-logo.png" alt="QuillFire" className="w-10 h-10 rounded-lg" />
            <span className="text-2xl font-bold text-gray-900">QuillFire</span>
          </div>
          
          <div className="hidden md:flex items-center space-x-8">
            <a href="#how-it-works" className="text-gray-600 hover:text-gray-900 font-medium">How It Works</a>
            <a href="#features" className="text-gray-600 hover:text-gray-900 font-medium">Features</a>
            <a href="#pricing" className="text-gray-600 hover:text-gray-900 font-medium">Pricing</a>
            <a href="/blog" className="text-gray-600 hover:text-gray-900 font-medium">Blog</a>
          </div>

          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <>
                <Button onClick={() => navigate('/dashboard')} variant="outline" data-testid="dashboard-nav-btn">
                  Dashboard
                </Button>
                <Button onClick={onLogout} variant="ghost" data-testid="logout-btn">Logout</Button>
              </>
            ) : (
              <>
                <Button onClick={onLogin} variant="ghost" data-testid="login-nav-btn">Login</Button>
                <Button onClick={onLogin} className="bg-orange-600 text-white hover:bg-orange-700" data-testid="signup-nav-btn">
                  Start Free
                </Button>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-gray-600 hover:text-gray-900"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 space-y-4">
            <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block text-gray-600 hover:text-gray-900 font-medium py-2">How It Works</a>
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block text-gray-600 hover:text-gray-900 font-medium py-2">Features</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block text-gray-600 hover:text-gray-900 font-medium py-2">Pricing</a>
            <a href="/blog" onClick={() => setMobileMenuOpen(false)} className="block text-gray-600 hover:text-gray-900 font-medium py-2">Blog</a>
            <div className="pt-4 border-t border-gray-100">
              {user ? (
                <div className="space-y-3">
                  <Button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} variant="outline" className="w-full">
                    Dashboard
                  </Button>
                  <Button onClick={() => { onLogout(); setMobileMenuOpen(false); }} variant="ghost" className="w-full">Logout</Button>
                </div>
              ) : (
                <Button onClick={() => { onLogin(); setMobileMenuOpen(false); }} className="w-full bg-gray-900 text-white hover:bg-gray-800">
                  Login
                </Button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-20 px-4 md:px-6 relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center space-y-6 md:space-y-8">
            <div className="inline-flex items-center space-x-2 bg-orange-100 text-orange-700 px-3 md:px-4 py-2 rounded-full text-xs md:text-sm font-semibold">
              ⚡ People don&apos;t type keywords anymore. They ask full questions.
            </div>
              
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 px-4" style={{fontFamily: 'Poppins, sans-serif', lineHeight: '1.15'}} data-testid="hero-headline">
              Be the
              <br/>
              <span 
                className={`inline-block text-orange-600 transition-all duration-500 ease-in-out ${isNicheFading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
              >
                {rotatingNiches[currentNicheIndex].text}
              </span>
              <br/>
              <span className="text-gray-900">AI Recommends.</span>
            </h1>
              
            <p className="text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto font-normal px-4">
              QuillFire discovers the real questions people ask Google and AI tools like <span className="font-semibold text-gray-700">ChatGPT</span>, <span className="font-semibold text-gray-700">Claude</span>, and <span className="font-semibold text-gray-700">Grok</span> - then generates publish-ready answers built to rank and get cited.
            </p>

            <div className="flex flex-col items-center gap-3 md:gap-4 px-4">
              <Button 
                onClick={handleGetStarted} 
                size="lg" 
                className="bg-orange-600 text-white hover:bg-orange-700 text-base md:text-lg lg:text-xl px-8 md:px-12 py-5 md:py-7 rounded-xl shadow-lg hover:shadow-xl font-bold w-full sm:w-auto"
                data-testid="hero-cta-btn"
              >
                Start Free Trial (5 Posts) →
              </Button>
              <p className="text-xs md:text-sm text-gray-500 text-center">✓ No credit card required  •  ✓ 5 answer posts + 5 images included  •  ✓ Real questions + search volume included</p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 px-6 bg-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900" style={{fontFamily: 'Poppins, sans-serif'}}>
              The Problem: You&apos;re Invisible to Both Google AND AI
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              You know you should be publishing - but when you don&apos;t know what to write first, nothing goes up. And if nothing gets posted, you stay invisible.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="text-3xl mb-3">❌</div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">You're guessing at topics</h3>
              <p className="text-gray-600">You write "5 Benefits of Our Service" when prospects are asking specific questions about pricing, comparisons, and results.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="text-3xl mb-3">❌</div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">No one finds your content</h3>
              <p className="text-gray-600">Random content doesn't rank on Google. And AI search tools? They don't even know you exist.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="text-3xl mb-3">❌</div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">ChatGPT alone isn't enough</h3>
              <p className="text-gray-600">It can write anything, but it has NO IDEA what your prospects are actually searching for. You're creating content in the dark.</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <div className="text-3xl mb-3">❌</div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">Competitors are getting found instead</h3>
              <p className="text-gray-600">When prospects ask Google or AI for recommendations, your competitors show up - not you.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="how-it-works" className="py-20 px-6 bg-white" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ✅ HOW IT WORKS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Poppins, sans-serif'}}>
              From Setup to Published in Minutes
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your static website isn't going to rank for these questions. Answer them with a simple 4-step workflow.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-16">
            {/* Step 1: Profile Setup */}
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-orange-600" />
              </div>
              <div className="text-sm font-bold text-orange-600 mb-2">STEP 1</div>
              <h3 className="text-lg font-bold mb-3 text-gray-900">Build Your Profile</h3>
              <p className="text-gray-600 text-sm">Tell QuillFire about your business - services, location, audience, voice - so every post is personalized.</p>
            </div>

            {/* Step 2: Question Discovery */}
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-orange-600" />
              </div>
              <div className="text-sm font-bold text-orange-600 mb-2">STEP 2</div>
              <h3 className="text-lg font-bold mb-3 text-gray-900">Question Discovery</h3>
              <p className="text-gray-600 text-sm">Get 150+ real questions people ask in your market (with search volume), across local, pricing, and comparison intent.</p>
            </div>

            {/* Step 3: One-Click Answer Creation */}
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-orange-600" />
              </div>
              <div className="text-sm font-bold text-orange-600 mb-2">STEP 3</div>
              <h3 className="text-lg font-bold mb-3 text-gray-900">One-Click Answers</h3>
              <p className="text-gray-600 text-sm">Click a question. Generate a complete 1,600-2,300 word answer post with your business details, CTA, and internal links.</p>
            </div>

            {/* Step 4: Publish Anywhere */}
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-8 h-8 text-orange-600" />
              </div>
              <div className="text-sm font-bold text-orange-600 mb-2">STEP 4</div>
              <h3 className="text-lg font-bold mb-3 text-gray-900">Publish Anywhere</h3>
              <p className="text-gray-600 text-sm">Export in one click - or publish directly to your website and platforms on Pro.</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
            {/* Business Type Selector */}
            <div className="flex flex-wrap justify-center gap-2 mb-8">
              {businessExamples.map((biz, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentExample(index)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    currentExample === index
                      ? 'bg-orange-600 text-white shadow-md'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {biz.icon} {biz.name}
                </button>
              ))}
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4 min-h-[72px] md:min-h-[36px]">
                  <span className="text-3xl mr-2">{example.icon}</span> 
                  Example: {example.name}
                </h3>
                <p className="text-gray-600 mb-6">Instead of guessing what to write about, you see exactly what prospects search&nbsp;for:</p>
                <div className="space-y-3">
                  {example.searches.map((search, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-4 border border-gray-200 transition-all hover:shadow-md h-[100px] md:h-[84px]">
                      <p className="text-sm font-semibold text-orange-600 mb-1">{search.type} ({search.volume} searches/mo)</p>
                      <p className="text-gray-900">"{search.query}"</p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 border-2 border-orange-200">
                <p className="text-sm font-semibold text-gray-500 mb-4">ONE-CLICK BLOG OUTPUT:</p>
                <h4 className="text-xl font-bold text-gray-900 mb-3 h-[84px] md:h-[56px]">{example.blogTitle}</h4>
                <p className="text-gray-600 text-sm mb-4">1,600+ words • SEO optimized • Ready to&nbsp;publish</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Answers the exact question prospects search</li>
                  <li>✓ Includes your business name & location</li>
                  <li>✓ Complete with meta title, description, tags</li>
                  <li>✓ Structured for Google AND AI&nbsp;citation</li>
                </ul>
              </div>
            </div>

            {/* Auto-rotate indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {businessExamples.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    currentExample === index ? 'bg-orange-600 w-6' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why AI Search Matters */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{fontFamily: 'Poppins, sans-serif'}}>
              People Talk to AI Like a Personal Assistant.<br/>
              <span className="text-orange-400">Is Your Business the Answer?</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Millions now ask ChatGPT, Siri, Alexa, and Google Assistant for recommendations. These AI tools cite websites with authoritative, well-structured content. QuillFire makes sure that's <em>your</em> website.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
              <div className="text-sm text-gray-400 mb-2">Old Way: Keywords</div>
              <div className="text-2xl font-bold text-white mb-2">"plumber austin"</div>
              <div className="text-gray-400 mb-2">Stuffing keywords into pages and hoping Google finds you</div>
              <div className="text-sm text-red-400">❌ Outdated and increasingly ineffective</div>
            </div>

            <div className="bg-orange-600/20 backdrop-blur rounded-xl p-6 border-2 border-orange-500">
              <div className="text-sm text-orange-300 mb-2">New Way: Question Discovery</div>
              <div className="text-2xl font-bold text-white mb-2">"What should I do if my water heater is leaking?"</div>
              <div className="text-orange-100 mb-2">Answer the exact questions people ask AI assistants</div>
              <div className="text-sm text-green-400">✓ How people actually search in 2025</div>
            </div>
          </div>

          <div className="text-center">
            <p className="text-xl text-gray-300 mb-8">
              <strong className="text-white">Voice search. AI assistants. Conversational queries.</strong> The businesses answering real questions will dominate the next decade of search.
            </p>
            <Button 
              onClick={handleGetStarted}
              size="lg"
              className="bg-orange-600 text-white hover:bg-orange-700 text-lg px-10 py-6 rounded-xl"
            >
              Start Answering Questions →
            </Button>
          </div>
        </div>
      </section>

      {/* Why Our Blogs Are Better Section - NEW */}
      <section id="features" className="py-20 px-6 bg-gray-50" data-testid="quality-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Poppins, sans-serif'}}>
              Not Just Content. A Ready-to-Publish SEO&nbsp;Asset.
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Anyone can generate text. QuillFire generates complete posts with the pieces that actually help you rank and&nbsp;convert.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-start gap-3">
              <div className="text-green-500 mt-0.5">✓</div>
              <div>
                <p className="font-semibold text-gray-900">1,600–2,300 words</p>
                <p className="text-sm text-gray-600">Long-form answers that cover the full question</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-start gap-3">
              <div className="text-green-500 mt-0.5">✓</div>
              <div>
                <p className="font-semibold text-gray-900">SEO title + meta description</p>
                <p className="text-sm text-gray-600">Optimized for click-throughs in search results</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-start gap-3">
              <div className="text-green-500 mt-0.5">✓</div>
              <div>
                <p className="font-semibold text-gray-900">Structured headings (H1/H2/H3)</p>
                <p className="text-sm text-gray-600">Matches how people search and scan</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-start gap-3">
              <div className="text-green-500 mt-0.5">✓</div>
              <div>
                <p className="font-semibold text-gray-900">Natural keyword placement</p>
                <p className="text-sm text-gray-600">No stuffing - flows naturally</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-start gap-3">
              <div className="text-green-500 mt-0.5">✓</div>
              <div>
                <p className="font-semibold text-gray-900">FAQ block included</p>
                <p className="text-sm text-gray-600">Great for voice/conversational search</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-start gap-3">
              <div className="text-green-500 mt-0.5">✓</div>
              <div>
                <p className="font-semibold text-gray-900">Custom CTA for your audience</p>
                <p className="text-sm text-gray-600">Pulled from your business + target audience</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-start gap-3">
              <div className="text-green-500 mt-0.5">✓</div>
              <div>
                <p className="font-semibold text-gray-900">Internal links suggested</p>
                <p className="text-sm text-gray-600">From your website pages library</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-start gap-3">
              <div className="text-green-500 mt-0.5">✓</div>
              <div>
                <p className="font-semibold text-gray-900">Optional featured image</p>
                <p className="text-sm text-gray-600">AI-generated to match the topic</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 border border-gray-200 flex items-start gap-3">
              <div className="text-green-500 mt-0.5">✓</div>
              <div>
                <p className="font-semibold text-gray-900">Export anywhere</p>
                <p className="text-sm text-gray-600">HTML / Word / Copy-Paste / One-click publish</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Business Profile Powers Everything - NEW */}
      <section className="py-20 px-6 bg-white" data-testid="profile-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Poppins, sans-serif'}}>
              Your Business Profile Powers&nbsp;Everything
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              QuillFire doesn&apos;t write generic content. It uses your Business Profile to generate answer posts that sound like you and drive&nbsp;actions.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 border border-orange-100">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <MessageSquare className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">CTA Library</h3>
              <p className="text-gray-600 mb-3">CTAs that match the topic and audience.</p>
              <p className="text-sm text-gray-500">Create CTAs for parents, homeowners, buyers, etc. QuillFire automatically inserts the right CTA into each post.</p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 border border-orange-100">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <Link2 className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Links Library</h3>
              <p className="text-gray-600 mb-3">Automatic internal links from your website.</p>
              <p className="text-sm text-gray-500">QuillFire saves your key pages - so each answer post can link to the right services and conversion pages.</p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white rounded-xl p-6 border border-orange-100">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4">
                <Sparkles className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Brand Voice + USPs</h3>
              <p className="text-gray-600 mb-3">Your tone, your selling points, your location.</p>
              <p className="text-sm text-gray-500">Friendly, professional, bold - QuillFire writes in your voice and includes what makes you different.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 px-6 bg-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{fontFamily: 'Poppins, sans-serif'}}>Real Results From Real Businesses</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center font-bold text-orange-700 mr-3">
                  MR
                </div>
                <div>
                  <p className="font-bold text-gray-900">Marcus R.</p>
                  <p className="text-sm text-gray-600">HVAC Company Owner</p>
                </div>
              </div>
              <div className="text-yellow-500 mb-3">⭐⭐⭐⭐⭐</div>
              <p className="text-gray-700 italic">"I was spending hours trying to figure out what to write about. Now I see exactly what parents are searching for and create content in minutes."</p>
              <p className="text-orange-700 font-semibold mt-3">Now ranking for 12 new keywords</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center font-bold text-orange-700 mr-3">
                  SC
                </div>
                <div>
                  <p className="font-bold text-gray-900">Sarah C.</p>
                  <p className="text-sm text-gray-600">Local Service Business</p>
                </div>
              </div>
              <div className="text-yellow-500 mb-3">⭐⭐⭐⭐⭐</div>
              <p className="text-gray-700 italic">"The search discovery feature is a game-changer. I had no idea people were searching for these specific questions. Now I answer them."</p>
              <p className="text-orange-700 font-semibold mt-3">Traffic up 47% in 2 months</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center font-bold text-orange-700 mr-3">
                  JP
                </div>
                <div>
                  <p className="font-bold text-gray-900">James P.</p>
                  <p className="text-sm text-gray-600">Professional Services</p>
                </div>
              </div>
              <div className="text-yellow-500 mb-3">⭐⭐⭐⭐⭐</div>
              <p className="text-gray-700 italic">"Finally, a tool that tells me WHAT to write about, not just how to write it. The AI-generated content is actually good."</p>
              <p className="text-orange-700 font-semibold mt-3">Saves 10+ hours per week</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center font-bold text-orange-700 mr-3">
                  LM
                </div>
                <div>
                  <p className="font-bold text-gray-900">Lisa M.</p>
                  <p className="text-sm text-gray-600">Dental Practice Manager</p>
                </div>
              </div>
              <div className="text-yellow-500 mb-3">⭐⭐⭐⭐⭐</div>
              <p className="text-gray-700 italic">"We published 8 answer posts last month answering questions like 'how much do veneers cost' and 'Invisalign vs braces.' Two are already on page one."</p>
              <p className="text-orange-700 font-semibold mt-3">2 page-one rankings in 30 days</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center font-bold text-orange-700 mr-3">
                  DT
                </div>
                <div>
                  <p className="font-bold text-gray-900">David T.</p>
                  <p className="text-sm text-gray-600">HVAC Company Owner</p>
                </div>
              </div>
              <div className="text-yellow-500 mb-3">⭐⭐⭐⭐⭐</div>
              <p className="text-gray-700 italic">"My website guy wanted $500 per article. QuillFire costs less than one of those and I can make unlimited content. No brainer."</p>
              <p className="text-orange-700 font-semibold mt-3">Saving $2,000+/month on content</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center font-bold text-orange-700 mr-3">
                  RK
                </div>
                <div>
                  <p className="font-bold text-gray-900">Rachel K.</p>
                  <p className="text-sm text-gray-600">Real Estate Agent</p>
                </div>
              </div>
              <div className="text-yellow-500 mb-3">⭐⭐⭐⭐⭐</div>
              <p className="text-gray-700 italic">"I used to stare at a blank screen for hours. Now I pick a question my clients actually ask, hit generate, and have a polished answer post in 10 minutes."</p>
              <p className="text-orange-700 font-semibold mt-3">Publishing 3x more content</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-6 bg-white" data-testid="pricing-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Poppins, sans-serif'}}>Simple, Transparent Pricing</h2>
            <p className="text-lg text-gray-600">Try free. Upgrade when you're&nbsp;ready.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {/* Free Plan */}
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-8">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Free Trial</h3>
                <div className="text-4xl font-bold text-gray-900 mb-1">$0</div>
                <p className="text-gray-500 text-sm">forever</p>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700"><strong>5 answer posts</strong> (free trial)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700"><strong>5 AI images</strong> (free trial)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700"><strong>3 question discoveries</strong></span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Full SEO optimization</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Export & download</span>
                </li>
                <li className="flex items-start space-x-3 text-gray-400">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">✗</span>
                  <span>Auto-posting</span>
                </li>
                <li className="flex items-start space-x-3 text-gray-400">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">✗</span>
                  <span>Content platforms</span>
                </li>
              </ul>
              
              <Button 
                onClick={handleGetStarted}
                className="w-full py-6 text-lg bg-gray-900 text-white hover:bg-gray-800 font-semibold"
              >
                Start Free Trial
              </Button>
              <p className="text-xs text-gray-500 text-center mt-2">Test quality before you commit</p>
            </div>

            {/* Starter Plan */}
            <div className="bg-white rounded-2xl border-2 border-gray-200 p-8">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Starter</h3>
                <div className="text-4xl font-bold text-gray-900 mb-1">$27</div>
                <p className="text-gray-500 text-sm">per month</p>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700"><strong>30 answer posts/month</strong> (1/day)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700"><strong>30 AI images/month</strong></span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700"><strong>5 question discoveries/mo</strong></span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Full SEO optimization</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Content library</span>
                </li>
                <li className="flex items-start space-x-3 text-gray-400">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">✗</span>
                  <span>Auto-posting</span>
                </li>
                <li className="flex items-start space-x-3 text-gray-400">
                  <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">✗</span>
                  <span>Content platforms</span>
                </li>
              </ul>
              
              <Button 
                onClick={() => handleUpgrade('starter')}
                className="w-full py-6 text-lg bg-gray-900 text-white hover:bg-gray-800 font-semibold"
              >
                {user?.plan === 'starter' ? 'Current Plan' : 'Start with Starter'}
              </Button>
              <p className="text-xs text-gray-500 text-center mt-2">Perfect for consistent daily content</p>
            </div>

            {/* Pro Plan */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl border-2 border-orange-400 p-8 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-orange-600 text-white px-4 py-1 rounded-full text-sm font-bold">MOST POPULAR</span>
              </div>
              
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Pro</h3>
                <div className="text-4xl font-bold text-gray-900 mb-1">$67</div>
                <p className="text-gray-500 text-sm">per month</p>
              </div>
              
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-900"><strong>90 answer posts/month</strong> (3/day)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-900"><strong>90 AI images/month</strong></span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-900"><strong>20 question discoveries/mo</strong></span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Full SEO optimization</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Content library</span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-900"><strong>Auto-post to your website</strong></span>
                </li>
                <li className="flex items-start space-x-3">
                  <Check className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-900"><strong>Post to Medium, LinkedIn & GBP</strong></span>
                </li>
              </ul>
              
              <Button 
                onClick={() => handleUpgrade('pro')}
                className="w-full py-6 text-lg bg-orange-600 hover:bg-orange-700 text-white font-bold"
              >
                {user?.plan === 'pro' ? 'Current Plan' : 'Go Pro →'}
              </Button>
              <p className="text-xs text-gray-600 text-center mt-2">Best for serious content marketing</p>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-500 text-sm mb-2">All plans include a 14-day money-back guarantee</p>
            <p className="text-gray-500 text-sm">Questions? Email us at support@quillfire.ai</p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-gray-100" id="faq">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{fontFamily: 'Poppins, sans-serif'}}>
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">What do you mean by "question discovery"?</h3>
              <p className="text-gray-600">Instead of guessing what to write about, QuillFire shows you the exact questions people are asking about your industry - the natural, conversational queries they speak into Siri, type into ChatGPT, or search on Google. These are real questions with real search volume.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">How is this different from traditional keyword research?</h3>
              <p className="text-gray-600">Traditional keyword tools show you generic terms like "plumber austin." QuillFire shows you the questions people actually ask: "Why is my water heater making a popping sound?" or "How much does it cost to fix a leaky faucet in Austin?" These conversational queries are what voice search and AI assistants optimize for.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Will this actually help me rank on Google AND AI search?</h3>
              <p className="text-gray-600">Yes. Google increasingly favors content that directly answers questions (that's what featured snippets are). AI tools like ChatGPT and Perplexity cite websites with authoritative, well-structured content. By creating comprehensive answers to real questions, you're optimizing for both.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">How long until I see results?</h3>
              <p className="text-gray-600">Most businesses start seeing improved rankings within 4-8 weeks of consistent publishing. The key is targeting the right questions (which we show you) and publishing regularly. Even 1 post per day adds up to 30 pieces of targeted content per&nbsp;month.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Can I edit the content before publishing?</h3>
              <p className="text-gray-600">Absolutely. Every answer post is saved to your Content Library where you can edit, add your own voice, include specific details, and publish when ready. Think of the AI as your first draft - you make it&nbsp;yours.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">What counts as a "question discovery search"?</h3>
              <p className="text-gray-600">Each time you click "Generate Content Strategy" to discover questions in your market, that counts as one search. Free users get 3 total, Starter gets 5/month, and Pro gets 10/month. We recommend filling out your complete business profile before your first search to maximize results.</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-2">What if I want to cancel?</h3>
              <p className="text-gray-600">Cancel anytime - no contracts, no hassle. Paid plans include a 14-day money-back guarantee. And the free trial gives you 5 answer posts to test quality before you commit to&nbsp;anything.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Magnet Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 md:p-12 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
            <div className="relative z-10">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <div className="text-sm font-bold uppercase tracking-wide text-orange-100 mb-2">Free Download</div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-3">50 Questions Customers Ask Before They&nbsp;Buy</h3>
                  <p className="text-orange-100 mb-4">Pricing, comparisons, &quot;near me,&quot; and problem-based searches - based on real demand&nbsp;data.</p>
                  <ul className="text-sm text-orange-100 space-y-1 mb-6">
                    <li>✓ Real search queries with volume data</li>
                    <li>✓ Pricing, comparison & local intent questions</li>
                    <li>✓ Ready to turn into ranking content</li>
                  </ul>
                </div>
                <div className="w-full md:w-auto">
                  <div className="bg-white rounded-xl p-6 text-gray-900">
                    {leadSuccess ? (
                      <div className="text-center py-4">
                        <div className="text-green-600 text-2xl mb-2">✓</div>
                        <p className="text-sm font-medium text-gray-900 mb-1">Download started!</p>
                        <p className="text-xs text-gray-500">Check your downloads folder.</p>
                      </div>
                    ) : (
                      <form onSubmit={handleLeadMagnetSubmit}>
                        <p className="text-sm font-medium text-gray-600 mb-3">Get the 50 questions:</p>
                        <input 
                          type="email" 
                          placeholder="Enter your email" 
                          value={leadEmail}
                          onChange={(e) => setLeadEmail(e.target.value)}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-3 text-sm"
                        />
                        <Button 
                          type="submit"
                          disabled={leadSubmitting}
                          className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 disabled:opacity-50"
                        >
                          {leadSubmitting ? 'Sending...' : 'Send Me the 50 Questions →'}
                        </Button>
                        <p className="text-xs text-gray-500 mt-2 text-center">No spam. Unsubscribe anytime.</p>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6 bg-orange-600 text-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{fontFamily: 'Poppins, sans-serif'}}>
            Be the Answer. Get&nbsp;Recommended.
          </h2>
          <p className="text-xl mb-8 text-orange-50">
            Your customers are asking questions right now. Be the business Google and AI tools recommend.
          </p>
          
          <Button 
            onClick={handleGetStarted}
            size="lg"
            className="bg-white text-orange-600 hover:bg-gray-50 text-xl px-12 py-7 rounded-xl shadow-xl font-bold"
            data-testid="final-cta-btn"
          >
            Start Free Trial (5 Blogs) →
          </Button>
          <p className="text-sm text-orange-100 mt-4">✓ No credit card required  •  ✓ 5 blogs + 5 images included  •  ✓ Real questions + search volume</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <img src="/quillfire-logo.png" alt="QuillFire" className="w-8 h-8 rounded-lg" />
            <span className="text-xl font-bold text-white">QuillFire</span>
          </div>
          <p className="text-sm">&copy; 2025 QuillFire. All rights reserved.</p>
          <div className="mt-4 space-x-6">
            <a href="#" className="hover:text-white">Privacy Policy</a>
            <a href="#" className="hover:text-white">Terms of Service</a>
            <a href="#" className="hover:text-white">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
